import React, { useMemo, useCallback, useState } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardAction,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { DownloadIcon } from '@phosphor-icons/react';
import type {
  ReportTable as ReportTableType,
  ReportTimeFilter,
} from '@erp/shared-types';
import { TimeFilterPresetSelector } from '@/components/rebates/TimeFilterPreset';
import type { TimeFilterPreset } from '@/utils/timeFilterPresets';
import { ALL_MONTHS } from '@/utils/rebateForecastingUtils';
import BreakdownTable, {
  type BreakdownCell,
  type BreakdownCellContext,
} from '@/components/ui/breakdown-tables/BreakdownTable';
import Papa from 'papaparse';
import { formatCurrency } from '@erp/shared-utils';

type SortDirection = 'asc' | 'desc' | null;

interface ReportTableProps {
  tableName?: string;
  table: ReportTableType;
  timeFilter: ReportTimeFilter;
  onTimeFilterChange: (filter: ReportTimeFilter) => void;
  onExport?: () => void;
  showTotals?: boolean;
  rowLabel?: string; // Label for the row dimension (default: "Category")
  showTimeFilter?: boolean; // Show time filter controls (default: true)
  renderCell?: (context: BreakdownCellContext) => React.ReactNode; // Custom cell renderer
  enableSorting?: boolean; // Enable column sorting (default: true)
  defaultSortColumn?: string; // Default column to sort by (e.g., "Dec" or last visible column)
  defaultSortDirection?: 'asc' | 'desc'; // Default sort direction (default: 'desc')
}

/**
 * Reusable component for displaying report tables with time filtering and sorting
 * Uses BreakdownTable component for consistent styling
 */
export const ReportTable: React.FC<ReportTableProps> = ({
  table,
  tableName,
  timeFilter,
  onTimeFilterChange,
  onExport,
  showTotals = true,
  rowLabel = 'Category',
  showTimeFilter = true,
  renderCell: customRenderCell,
  enableSorting = true,
  defaultSortColumn,
  defaultSortDirection = 'desc',
}) => {
  // Sort state
  const [sortColumnIndex, setSortColumnIndex] = useState<number | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);
  // Determine which columns to show based on time filter
  // Check if this is a month-based table by looking for month patterns in headers
  // A month-based table has headers like "Jan 2024", "Feb 2024", etc. or just "Jan", "Feb", etc.
  const monthPatternWithYear = useMemo(
    () => /^(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\s+\d{4}$/,
    [],
  );
  const monthPatternWithoutYear = useMemo(
    () => /^(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)$/,
    [],
  );
  const isMonthHeader = useCallback(
    (header: string): boolean => {
      return (
        monthPatternWithYear.test(header) ||
        monthPatternWithoutYear.test(header)
      );
    },
    [monthPatternWithYear, monthPatternWithoutYear],
  );
  const hasMonthHeaders = table.columnHeaders.some(isMonthHeader);
  const isMonthBasedTable = hasMonthHeaders;

  const visibleColumnIndices = useMemo(() => {
    // If not a month-based table, show all columns
    if (!isMonthBasedTable) {
      return Array.from({ length: table.columnHeaders.length }, (_, i) => i);
    }

    // Find which columns are month columns (those matching the month pattern)
    const monthColumnIndices: number[] = [];
    const nonMonthColumnIndices: number[] = [];

    table.columnHeaders.forEach((header, index) => {
      if (isMonthHeader(header)) {
        monthColumnIndices.push(index);
      } else {
        nonMonthColumnIndices.push(index);
      }
    });

    const currentMonthIndex = new Date().getMonth();
    const monthNames = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ];

    // Determine which month columns to show based on time filter
    let visibleMonthIndices: number[] = [];
    switch (timeFilter) {
      case 'ytd': {
        // Show Jan through current month
        // Find month columns that are Jan through current month
        visibleMonthIndices = monthColumnIndices.filter((colIndex) => {
          const header = table.columnHeaders[colIndex];
          const monthMatch = header.match(
            /^(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)/,
          );
          if (!monthMatch) return false;
          const monthName = monthMatch[1];
          const monthIdx = monthNames.indexOf(monthName);
          return monthIdx >= 0 && monthIdx <= currentMonthIndex;
        });
        break;
      }
      case 'current': {
        // Show only current month
        const currentMonthName = monthNames[currentMonthIndex];
        visibleMonthIndices = monthColumnIndices.filter((colIndex) => {
          const header = table.columnHeaders[colIndex];
          return header.startsWith(currentMonthName);
        });
        break;
      }
      case 'all':
      default: {
        // Show all month columns
        visibleMonthIndices = monthColumnIndices;
        break;
      }
    }

    // Always include non-month columns (like Total YTD, Post Y/E Adj #1)
    // Combine non-month columns with visible month columns, maintaining order
    const allVisibleIndices: number[] = [];
    table.columnHeaders.forEach((_, index) => {
      if (nonMonthColumnIndices.includes(index)) {
        allVisibleIndices.push(index);
      } else if (visibleMonthIndices.includes(index)) {
        allVisibleIndices.push(index);
      }
    });

    // Debug logging
    if (process.env.NODE_ENV === 'development') {
      console.log('[ReportTable] Column visibility debug:', {
        totalColumns: table.columnHeaders.length,
        isMonthBasedTable,
        timeFilter,
        monthColumnIndices,
        nonMonthColumnIndices,
        visibleMonthIndices,
        allVisibleIndices,
        columnHeaders: table.columnHeaders,
      });
    }

    return allVisibleIndices;
  }, [isMonthBasedTable, isMonthHeader, table.columnHeaders, timeFilter]);

  // Custom cell renderer that colors based on lineType and uses formattedValue from backend
  const defaultRenderCell = useCallback(
    (context: BreakdownCellContext): React.ReactNode => {
      const { cell, column } = context;
      const value = cell.value;
      const metadata = cell.metadata as
        | {
            lineType?: string;
            isFutureMonth?: boolean;
            formattedValue?: string;
          }
        | undefined;

      // Handle null/undefined values
      if (value === null || value === undefined) {
        return <span className="text-xs text-muted-foreground">-</span>;
      }

      // Handle "Supplier Code" and "Division Code" columns as text (not currency)
      if (column === 'Supplier Code' || column === 'Division Code') {
        const codeValue = metadata?.formattedValue || '';
        if (!codeValue) {
          return <span className="text-xs text-muted-foreground">-</span>;
        }
        return (
          <div className="text-xs font-medium text-foreground">{codeValue}</div>
        );
      }

      // Determine color based on lineType
      let textColor = 'text-foreground'; // Default
      if (metadata?.lineType === 'reported') {
        textColor = 'text-black'; // Black for reported
      } else if (metadata?.lineType === 'predictions') {
        textColor = 'text-blue-600'; // Blue for predictions
      } else if (metadata?.lineType === 'estimates') {
        textColor = 'text-green-600'; // Green for estimates
      }

      // Use formattedValue from backend if available, otherwise format on frontend
      const displayValue =
        metadata?.formattedValue ??
        formatCurrency(typeof value === 'number' ? value : 0);

      return (
        <div className={`text-xs font-medium ${textColor}`}>{displayValue}</div>
      );
    },
    [],
  );

  // Use custom renderCell if provided, otherwise use default
  const renderCell = customRenderCell || defaultRenderCell;

  // Initialize default sort if specified
  React.useEffect(() => {
    if (defaultSortColumn && sortColumnIndex === null && enableSorting) {
      const columnIndex = table.columnHeaders.indexOf(defaultSortColumn);
      if (columnIndex !== -1) {
        setSortColumnIndex(columnIndex);
        setSortDirection(defaultSortDirection);
      } else if (visibleColumnIndices.length > 0) {
        // Default to last visible column
        const lastVisibleIndex =
          visibleColumnIndices[visibleColumnIndices.length - 1];
        setSortColumnIndex(lastVisibleIndex);
        setSortDirection(defaultSortDirection);
      }
    }
  }, [
    defaultSortColumn,
    defaultSortDirection,
    enableSorting,
    table.columnHeaders,
    visibleColumnIndices,
    sortColumnIndex,
  ]);

  // Sort rows based on selected column
  const sortedRows = useMemo(() => {
    if (!enableSorting || sortColumnIndex === null || sortDirection === null) {
      return table.rows;
    }

    const columnHeader = table.columnHeaders[sortColumnIndex];
    const isCodeColumn =
      columnHeader === 'Division Code' || columnHeader === 'Supplier Code';

    const sorted = [...table.rows].sort((a, b) => {
      const aCell = a.cells[sortColumnIndex];
      const bCell = b.cells[sortColumnIndex];

      // For code columns, use formattedValue for string comparison
      if (isCodeColumn) {
        const aStr = aCell?.formattedValue ?? '';
        const bStr = bCell?.formattedValue ?? '';
        // Case-insensitive comparison using localeCompare options
        const comparison = aStr.localeCompare(bStr, undefined, {
          sensitivity: 'base',
        });
        return sortDirection === 'asc' ? comparison : -comparison;
      }

      // For numeric columns, use numeric comparison
      const aValue = aCell?.value ?? 0;
      const bValue = bCell?.value ?? 0;
      const aNum = typeof aValue === 'number' ? aValue : 0;
      const bNum = typeof bValue === 'number' ? bValue : 0;

      return sortDirection === 'asc' ? aNum - bNum : bNum - aNum;
    });

    return sorted;
  }, [
    table.rows,
    sortColumnIndex,
    sortDirection,
    enableSorting,
    table.columnHeaders,
  ]);

  // Handle sort column change
  const handleSortChange = useCallback(
    (columnHeader: string) => {
      const columnIndex = table.columnHeaders.indexOf(columnHeader);
      if (columnIndex === -1) return;

      if (sortColumnIndex === columnIndex) {
        // Toggle direction or clear sort
        if (sortDirection === 'desc') {
          setSortDirection('asc');
        } else if (sortDirection === 'asc') {
          setSortDirection(null);
          setSortColumnIndex(null);
        }
      } else {
        // Sort by new column (desc by default)
        setSortColumnIndex(columnIndex);
        setSortDirection('desc');
      }
    },
    [sortColumnIndex, sortDirection, table.columnHeaders],
  );

  // Transform ReportTable data into BreakdownCell[] format
  const breakdownData = useMemo<BreakdownCell[]>(() => {
    const cells: BreakdownCell[] = [];

    // Iterate through sorted rows
    sortedRows.forEach((row) => {
      // Only include cells for visible columns (based on time filter)
      visibleColumnIndices.forEach((columnIndex) => {
        const cell = row.cells[columnIndex];
        // Pass metadata (lineType, isFutureMonth, and formattedValue) to BreakdownCell
        const metadata: Record<string, unknown> = {};
        if (cell?.lineType) {
          metadata.lineType = cell.lineType;
        }
        if (cell?.isFutureMonth !== undefined) {
          metadata.isFutureMonth = cell.isFutureMonth;
        }
        if (cell?.formattedValue) {
          metadata.formattedValue = cell.formattedValue;
        }
        // Always include cells, even if they're 0 or undefined (will show as 0 or -)
        cells.push({
          column: table.columnHeaders[columnIndex],
          subColumn: null,
          row: row.label,
          subRow: null,
          value: cell?.value ?? 0,
          metadata: Object.keys(metadata).length > 0 ? metadata : undefined,
        });
      });
    });

    return cells;
  }, [sortedRows, table.columnHeaders, visibleColumnIndices]);

  // Determine column order based on visible columns
  const columnOrder = useMemo(() => {
    return visibleColumnIndices.map((index) => table.columnHeaders[index]);
  }, [visibleColumnIndices, table.columnHeaders]);

  // Determine row order for BreakdownTable
  const rowOrder = useMemo(() => {
    return sortedRows.map((row) => row.label);
  }, [sortedRows]);

  const handleExportCSV = () => {
    if (onExport) {
      onExport();
      return;
    }

    // Helper to format numeric values to exactly 2 decimal places
    const formatValue = (value: number | string | null | undefined): string => {
      if (value === null || value === undefined) return '0.00';
      if (typeof value === 'number') {
        return value.toFixed(2);
      }
      // Try to parse as number for string values
      const parsed = parseFloat(value);
      if (!isNaN(parsed)) {
        return parsed.toFixed(2);
      }
      return value;
    };

    // Generate CSV matching BreakdownTable format
    try {
      const csvRows: string[][] = [];

      // Header row: rowLabel + month columns (no Total column)
      const headerRow: string[] = [rowLabel, ...columnOrder];
      csvRows.push(headerRow);

      // Data rows (no row totals) - use sorted rows
      sortedRows.forEach((row) => {
        const rowData: string[] = [row.label];

        // Add cell values for visible columns
        visibleColumnIndices.forEach((index) => {
          const cell = row.cells[index];
          rowData.push(cell ? formatValue(cell.value) : '0');
        });

        csvRows.push(rowData);
      });

      // Column totals row (bottom row) if enabled
      if (showTotals) {
        const columnTotalsRow: string[] = ['Total'];
        visibleColumnIndices.forEach((index) => {
          const columnTotal = sortedRows.reduce(
            (sum, row) => sum + (row.cells[index]?.value || 0),
            0,
          );
          columnTotalsRow.push(formatValue(columnTotal));
        });
        csvRows.push(columnTotalsRow);
      }

      // Generate CSV content
      const csvContent = Papa.unparse(csvRows);

      // Create filename with timestamp
      const timestamp = new Date().toISOString().split('T')[0];
      const baseFilename = table.title
        ? table.title.toLowerCase().replace(/\s+/g, '-')
        : 'report-table';
      const downloadFilename = `${baseFilename}-${timestamp}.csv`;

      // Create and trigger download
      const blob = new Blob([csvContent], {
        type: 'text/csv;charset=utf-8;',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = downloadFilename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting CSV:', error);
    }
  };

  // Get current sort column name for display
  const sortColumnName =
    sortColumnIndex !== null ? table.columnHeaders[sortColumnIndex] : null;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="flex flex-col gap-1">
            <CardTitle className="text-lg font-medium">
              {tableName ? tableName : table.title}
            </CardTitle>
          </div>
          <div className="flex items-center gap-3">
            {showTimeFilter && (
              <TimeFilterPresetSelector
                value={timeFilter as TimeFilterPreset}
                onChange={(preset) =>
                  onTimeFilterChange(preset as ReportTimeFilter)
                }
                visiblePresets={['ytd', 'all', 'current']}
              />
            )}
            <CardAction>
              <Button
                variant="outline"
                size="sm"
                onClick={handleExportCSV}
                className="h-8 px-2"
              >
                <DownloadIcon className="h-4 w-4" />
              </Button>
            </CardAction>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <BreakdownTable
          data={breakdownData}
          title=""
          showCard={false}
          rowLabel={rowLabel}
          columnLabel="Month"
          showXDim1Total={false}
          showYDim1Total={showTotals}
          xDimOrder={columnOrder}
          yDimOrder={rowOrder}
          maxHeight={700}
          fillMissingSubColumns={true}
          fillMissingSubRows={true}
          renderCell={renderCell}
          onColumnHeaderClick={enableSorting ? handleSortChange : undefined}
          sortColumn={sortColumnName}
          sortDirection={sortDirection}
        />
      </CardContent>
    </Card>
  );
};
